import { CAPTCHA_SCRIPT_ID, hasCaptchaConsent } from "./captcha-consent";

interface RecaptchaEnterprise {
  ready(callback: () => void): void;
  execute(siteKey: string, options: { action: string }): Promise<string>;
}
function enterprise(): RecaptchaEnterprise | undefined {
  return (
    window as Window & { grecaptcha?: { enterprise?: RecaptchaEnterprise } }
  ).grecaptcha?.enterprise;
}
function requireConsent(signal: AbortSignal) {
  if (signal.aborted || !hasCaptchaConsent())
    throw new Error("Verification cancelled");
}
function abortable<T>(promise: Promise<T>, signal: AbortSignal): Promise<T> {
  return new Promise((resolve, reject) => {
    const abort = () => reject(new Error("Verification cancelled"));
    if (signal.aborted) {
      abort();
      return;
    }
    signal.addEventListener("abort", abort, { once: true });
    promise
      .then(resolve, reject)
      .finally(() => signal.removeEventListener("abort", abort));
  });
}
let loaded: Promise<void> | undefined;
/** This is the only place Google is loaded. Called after valid, consenting submit. */
export async function acquireCaptchaToken(
  siteKey: string,
  signal: AbortSignal,
): Promise<string> {
  requireConsent(signal);
  if (!loaded)
    loaded = new Promise<void>((resolve, reject) => {
      const script = document.createElement("script");
      script.id = CAPTCHA_SCRIPT_ID;
      script.src = `https://www.google.com/recaptcha/enterprise.js?render=${encodeURIComponent(siteKey)}`;
      script.async = true;
      let settled = false;
      const fail = () => {
        if (settled) return;
        settled = true;
        clearTimeout(timer);
        script.remove();
        loaded = undefined;
        reject(new Error("Verification unavailable"));
      };
      const timer = setTimeout(fail, 10_000);
      script.onload = () => {
        if (settled) return;
        settled = true;
        clearTimeout(timer);
        resolve();
      };
      script.onerror = fail;
      document.head.appendChild(script);
    });
  await abortable(loaded, signal);
  requireConsent(signal);
  const api = enterprise();
  if (!api) throw new Error("Verification unavailable");
  await abortable(new Promise<void>((resolve) => api.ready(resolve)), signal);
  requireConsent(signal);
  const token = await abortable(
    api.execute(siteKey, { action: "contact_submit" }),
    signal,
  );
  requireConsent(signal);
  if (!token) throw new Error("Verification unavailable");
  return token;
}
